/* eslint-disable @typescript-eslint/explicit-function-return-type */
/* eslint-disable no-restricted-syntax */
import {
  FC,
  createContext,
  useContext,
  useState,
  useCallback,
  Dispatch,
  SetStateAction,
  useMemo,
  useEffect,
  useRef,
} from 'react';

import { v4 } from 'uuid';
import { AxiosError } from 'axios';
import { useToast } from '@chakra-ui/react';

import { useAuth } from '~/modules/auth/hooks/auth';

import { api } from '~/shared/services/api';

import { useHeader } from './header';
import { useDisplay } from './display';
import { resetTicketsBreak } from '../utils/resetTicketsBreak';
import { dedupeTicketsById } from '../utils/dedupeTicketsById';
import { resetSubItemsOnItems } from '../utils/resetSubItemsOnItems';
import { breakTickets } from '../utils/breakTickets';
import { applyCascateTicketSubItemStatusChange } from '../utils/applyCascateTicketSubItemStatusChange';
import { applyCascateTicketItemStatusChange } from '../utils/applyCascateTicketItemStatusChange';
import {
  ITicketItemExtractEvent,
  ITicketItemStatusChangeEvent,
  ITicketStatusChangeEvent,
  ITicketSubItemExtractEvent,
  ITicketSubItemStatusChangeEvent,
} from '../interfaces/IStatusChangeEvent';
import { IReplaceTicketItemEvent } from '../interfaces/IReplaceTicketItemEvent';
import { IPaginationResponseDTO } from '../interfaces/IPaginationResponseDTO';
import {
  IDisplayTicket,
  IDisplayTicketItem,
} from '../interfaces/IDisplayTicket';
import { TicketScheduledPeriodOptions } from '../enums/TicketScheduledPeriodOptions';
import { OrderStatusEnum } from '../enums/OrderStatusEnum';
import { HeaderOptionsEnum } from '../enums/HeaderOptionsEnum';

export interface LoadOrdersFiltersRequestDTO {
  status: OrderStatusEnum | HeaderOptionsEnum.scheduled;
  scheduledPeriod?: TicketScheduledPeriodOptions;
  page?: number;
  limit?: number;
}

export interface ILoadTicketsProsp {
  withLoad?: boolean;
  page?: number;
  display?: string;
  selectedInHeader?: HeaderOptionsEnum;
  scheduledPeriod?: TicketScheduledPeriodOptions;
  reset?: boolean;
  changindDisplayJourney?: HeaderOptionsEnum[];
}

interface ITicketsContextData {
  loading: boolean;
  tickets: IDisplayTicket[];
  currentPage: number;
  pagesCount: number;
  isAndroidDevice: boolean;
  resetDataToInitialValue: () => void;
  loadTickets(props: ILoadTicketsProsp): Promise<void>;
  changeTickets: Dispatch<SetStateAction<IDisplayTicket[]>>;
  receiveTicketFromSocket(ticket: IDisplayTicket): void;
  receiveTicketStatusChangeFromSocketFn(event: ITicketStatusChangeEvent): void;
  updateTicket(ticket: IDisplayTicket): void;
  receiveTicketItemStatusChangeFromSocketFn(
    event: ITicketItemStatusChangeEvent
  ): void;
  receiveTicketSubItemStatusChangeFromSocketFn(
    event: ITicketSubItemStatusChangeEvent
  ): void;
  receiveTicketItemReplaceFromSocketFn(event: IReplaceTicketItemEvent): void;
  receiveTicketItemExtractFromSocketFn(event: ITicketItemExtractEvent): void;
  receiveTicketSubItemExtractFromSocketFn(
    event: ITicketSubItemExtractEvent
  ): void;
}

const TicketsContext = createContext({} as ITicketsContextData);

const TicketsProvider: FC = ({ children }) => {
  const toast = useToast({
    status: 'error',
    position: 'top-right',
  });
  const { selectedInHeader, scheduledPeriod } = useHeader();
  const { display } = useDisplay();
  const initialized = useRef<boolean>(false);
  const [tickets, setTickets] = useState<IDisplayTicket[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [pagesCount, setPagesCount] = useState<number>();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const { signOut, configuration } = useAuth();

  /**
   * Função que injeta a "linha do catalogo" na listagem dos itens do ticket.
   *
   * Isso é necessário para que os tickets sejam exibidos corretamente
   * no componente de tickets quebráveis, onde os itens são organizados
   * por catalogo.
   */
  const injectCatalog = useCallback(
    (toInject: IDisplayTicket[]): IDisplayTicket[] => {
      return toInject.map((ticket) => {
        let organized: IDisplayTicketItem[] = [];

        const itemsWithoutCatalog =
          ticket.items.filter((item) => !item.catalog) || [];

        if (itemsWithoutCatalog.length) {
          organized = [...itemsWithoutCatalog];
        }

        const itemsWithCatalogs = ticket.items.filter(
          (where) => !!where.catalog
        );

        const catalogsIncluded = [
          // @ts-ignore
          ...new Set([...itemsWithCatalogs.map((item) => item.catalog)]),
        ];

        for (const catalog of catalogsIncluded) {
          const populated = (configuration?.settings?.catalogs || []).find(
            (where) => where._id === catalog
          );

          const index = itemsWithCatalogs.findIndex(
            (item) => item.catalog === catalog
          );
          itemsWithCatalogs.splice(index, 0, {
            catalogLine: {
              id: v4(),
              name: populated?.name || '',
              primaryColor: populated?.theme?.colors?.primary || '',
              onPrimaryColor: populated?.theme?.colors?.onPrimary || '',
              subitems: [],
            },
          } as unknown as IDisplayTicketItem);
        }

        organized = [...organized, ...itemsWithCatalogs];
        return {
          ...ticket,
          items: organized,
        };
      });
    },
    [configuration?.settings?.catalogs]
  );

  const orderTicketsByPriority = useCallback(
    (withoutOrdenation: IDisplayTicket[]): IDisplayTicket[] => {
      const sortTicket = (a: IDisplayTicket, b: IDisplayTicket) =>
        selectedInHeader === HeaderOptionsEnum.open
          ? new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
          : new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();

      const defaults =
        withoutOrdenation
          ?.filter((ticket) => !ticket.priority && !!ticket.items.length)
          ?.sort(sortTicket) || [];

      const withPriorities =
        withoutOrdenation?.filter(
          (ticket) => !!ticket.priority && !!ticket.items.length
        ) || [];

      const priorities = (
        [
          // @ts-ignore
          ...new Set([...withPriorities.map((ticket) => ticket.priority)]),
        ] as number[]
      )?.sort((a, b) => a - b);

      let sorted: IDisplayTicket[] = [];
      for (const priority of priorities) {
        const priorityTickets = withPriorities
          ?.filter((ticket) => ticket.priority === priority)
          ?.sort(sortTicket);

        sorted = [...sorted, ...priorityTickets];
      }

      sorted = [...sorted, ...defaults];
      return sorted;
    },
    [selectedInHeader]
  );

  const resetDataToInitialValue = useCallback(() => {
    setTickets([]);
    setPagesCount(0);
    setCurrentPage(1);
  }, []);

  const loadTickets = useCallback(
    async ({
      withLoad,
      page,
      selectedInHeader: filterStatus,
      scheduledPeriod: filterScheduledPeriod,
      display: filterDisplay,
      reset = false,
      changindDisplayJourney,
    }: ILoadTicketsProsp) => {
      if (!display) {
        return;
      }

      if (reset) {
        setCurrentPage(1);
      } else if (page) {
        setCurrentPage(page);
      }

      let statusOption = filterStatus || selectedInHeader;
      const scheduledPeriodOption = filterScheduledPeriod || scheduledPeriod;
      const displayOption = filterDisplay || display;

      // fallback in case state has not yet been updated
      if ((changindDisplayJourney || []).length) {
        const included =
          statusOption === HeaderOptionsEnum.scheduled ||
          changindDisplayJourney.includes(statusOption);
        if (!included) {
          // eslint-disable-next-line prefer-destructuring
          statusOption = changindDisplayJourney[0];
        }
      }

      const limit =
        statusOption === HeaderOptionsEnum.done ||
        statusOption === HeaderOptionsEnum.delivered ||
        statusOption === HeaderOptionsEnum.canceled
          ? 15
          : null;

      let status = statusOption || 'open';
      if (status === 'consolidate') {
        status = 'open';
      }

      const currentDisplay = configuration?.settings?.displays?.find(
        (configuredDisplay) => configuredDisplay._id === displayOption
      );
      const acceptScheduledOrders =
        currentDisplay?.acceptScheduledOrders === true;

      if (withLoad) setLoading(true);
      try {
        const { data: ordersResponse } = await api.get<
          IPaginationResponseDTO<IDisplayTicket[]>
        >(`/displays/${displayOption}/tickets`, {
          params: {
            status,
            ...(status === HeaderOptionsEnum.scheduled && {
              scheduledPeriod: scheduledPeriodOption,
            }),
            page: reset ? 1 : page || currentPage,
            limit,
          },
        });

        const filteredOrders =
          ordersResponse &&
          ordersResponse.data &&
          ordersResponse.data.length > 0
            ? ordersResponse.data.filter(
                (order) =>
                  order.items?.length > 0 &&
                  (acceptScheduledOrders || !order.shippingScheduledTo)
              )
            : [];

        setPagesCount(ordersResponse.pagesCount);
        setTickets((prevState) =>
          reset
            ? filteredOrders
            : dedupeTicketsById([...(prevState || []), ...filteredOrders])
        );
      } catch (error) {
        const axsError = error as AxiosError;
        if (axsError?.status === 401 || axsError?.response?.status === 401) {
          toast({
            status: 'warning',
            title: 'Ops',
            description:
              'Sua sessão parece ter expirado, faça o login novamente.',
          });

          signOut();
          return;
        }

        toast({
          status: 'error',
          title: 'Ops',
          description:
            'Houve um erro ao carregar os tickets, recarregue a página e tente novamente!',
        });
      } finally {
        if (withLoad) setLoading(false);
      }
    },
    [
      configuration?.settings?.displays,
      currentPage,
      display,
      scheduledPeriod,
      selectedInHeader,
      signOut,
      toast,
    ]
  );

  const updateTicket = useCallback((ticket: IDisplayTicket) => {
    setTickets((prev) =>
      prev.map((_ticket) => (_ticket.id === ticket.id ? ticket : _ticket))
    );
  }, []);

  const receiveTicketItemReplaceFromSocketFn = useCallback(
    (event: IReplaceTicketItemEvent) => {
      setTickets((prev) =>
        prev.map((ticket) => {
          if (ticket.id === event.ticketId) {
            return {
              ...ticket,
              items: [
                ...ticket.items.filter((item) => item.id !== event.itemId),
                ...event.items,
              ],
            };
          }
          return ticket;
        })
      );
    },
    []
  );

  const receiveTicketFromSocket = useCallback(
    (ticket: IDisplayTicket) => {
      setTickets((prev) => {
        let next = prev;
        if (
          !prev.some((t) => t.id === ticket.id) &&
          selectedInHeader.toString().includes(ticket.status.toString())
        ) {
          next = [...prev, ticket];
          if (ticket.transferredFrom) {
            next = next
              .map((tck) => {
                if (tck.id === ticket.transferredFrom) {
                  const transferredItems = ticket.items.map(
                    (item) => item.transferredFrom
                  );
                  return {
                    ...tck,
                    items: tck.items.filter(
                      (item) => !transferredItems.includes(item.id)
                    ),
                  };
                }
                return tck;
              })
              .filter((tck) => tck.items.length > 0);
          }
          new Audio('/notification.mp3').play().catch(() => null);
        }
        return next;
      });
    },
    [selectedInHeader]
  );

  const receiveTicketItemStatusChangeFromSocketFn = useCallback(
    (event: ITicketItemStatusChangeEvent) => {
      setTickets((prevState) =>
        prevState.map((ticket) => ({
          ...ticket,
          ...(ticket.id === event.ticketId && {
            items: ticket.items.map((item) => ({
              ...item,
              ...(item.id === event.itemId && {
                status: event.status,
                subitems: applyCascateTicketSubItemStatusChange(
                  item.subitems,
                  event.status
                ),
              }),
            })),
          }),
        }))
      );
    },
    []
  );

  const receiveTicketSubItemStatusChangeFromSocketFn = useCallback(
    (event: ITicketSubItemStatusChangeEvent) => {
      setTickets((prevState) =>
        prevState.map((ticket) => ({
          ...ticket,
          ...(ticket.id === event.ticketId && {
            items: ticket.items.map((item) => ({
              ...item,
              ...(item.id === event.itemId && {
                subitems: item.subitems.map((subItem) => ({
                  ...subItem,
                  ...(subItem.id === event.subitemId && {
                    status: event.status,
                  }),
                })),
              }),
            })),
          }),
        }))
      );
    },
    []
  );

  const receiveTicketStatusChangeFromSocketFn = useCallback(
    async (event: ITicketStatusChangeEvent) => {
      const { status } = event;

      if (status.includes('canceling')) {
        setTickets((prevState) =>
          prevState.map((ticket) => ({
            ...ticket,
            ...(ticket.id === event.ticketId && {
              status,
              items: applyCascateTicketItemStatusChange(
                ticket.items,
                OrderStatusEnum.canceled
              ),
            }),
          }))
        );

        return;
      }

      if ((status as unknown as HeaderOptionsEnum) !== selectedInHeader) {
        setTickets((prevState) =>
          prevState.filter((ticket) => ticket.id !== event.ticketId)
        );
      } else {
        const { data: ticket } = await api.get<IDisplayTicket>(
          `/tickets/${event.ticketId}`
        );

        setTickets((prevState) => [
          ...prevState.filter((_ticket) => _ticket.id !== event.ticketId),
          ticket,
        ]);
        if (
          event.status === OrderStatusEnum.open &&
          selectedInHeader === HeaderOptionsEnum.open
        )
          new Audio('/notification.mp3').play().catch(() => null);
      }
    },
    [selectedInHeader]
  );

  const receiveTicketItemExtractFromSocketFn = useCallback(
    (event: ITicketItemExtractEvent) => {
      if ((event.status as unknown as HeaderOptionsEnum) === selectedInHeader) {
        setTickets((prevState) =>
          dedupeTicketsById([...prevState, event.ticket])
        );
      } else {
        setTickets((prev) => [
          ...prev.map((ticket) =>
            ticket.id === event.ticketId
              ? {
                  ...ticket,
                  items: [
                    ...ticket.items
                      .filter((item) => !event.itemIds.includes(item.id))
                      .map((item) =>
                        event.itemWithSubitemsIds.includes(item.id)
                          ? {
                              ...item,
                              subitems: item.subitems.filter(
                                (subitem) =>
                                  !event.subitemIds.includes(subitem.id)
                              ),
                            }
                          : item
                      )
                      .filter(
                        (item) =>
                          !event.itemWithSubitemsIds.includes(item.id) ||
                          !!item.subitems.length
                      ),
                  ],
                }
              : ticket
          ),
        ]);
      }
    },
    [selectedInHeader]
  );

  const receiveTicketSubItemExtractFromSocketFn = useCallback(
    (event: ITicketSubItemExtractEvent) => {
      if ((event.status as unknown as HeaderOptionsEnum) === selectedInHeader) {
        setTickets((prevState) =>
          dedupeTicketsById([...prevState, event.ticket])
        );
      } else {
        setTickets((prev) => [
          ...prev.map((ticket) =>
            ticket.id === event.ticketId
              ? {
                  ...ticket,
                  items: ticket.items
                    .map((item) =>
                      item.id === event.itemId
                        ? {
                            ...item,
                            subitems: item.subitems.filter(
                              (subitem) =>
                                !event.subitemIds.includes(subitem.id)
                            ),
                          }
                        : item
                    )
                    .filter((item) => !!item.subitems.length),
                }
              : ticket
          ),
        ]);
      }
    },
    [selectedInHeader]
  );

  const brokenTickets = useMemo(() => {
    const [toReturn] = [tickets]
      .map(resetTicketsBreak)
      .map(orderTicketsByPriority)
      .map(injectCatalog)
      .map((ticketsToBreak) =>
        breakTickets(ticketsToBreak, {
          hasScheduledFilters: selectedInHeader === HeaderOptionsEnum.scheduled,
        })
      )
      .map(resetSubItemsOnItems);

    // if (toReturn.length > 2) {
    //   const lastId = toReturn[toReturn.length - 1]?.id;
    //   for (const tkt of toReturn) {
    //     if (tkt.id === lastId && tkt.hasContinuation) {
    //       Object.assign(tkt, {
    //         hasContinuation: false,
    //       });
    //     }
    //   }
    // }

    return toReturn;
  }, [injectCatalog, orderTicketsByPriority, selectedInHeader, tickets]);

  /**
   * Carga inicial do board.
   *
   * `initialized` é marcado ANTES da request e o efeito depende apenas do
   * display: a identidade de `loadTickets` muda assim que o `/companies`
   * responde (ele depende da configuração do auth), e marcar só no `.then()`
   * fazia o efeito rodar de novo com a primeira request ainda em voo — as duas
   * respostas caíam na lista e duplicavam todos os cards a cada reload.
   *
   * A troca de display pelo header não depende deste efeito: ela já reseta a
   * lista e chama `loadTickets` com `reset`.
   */
  useEffect(() => {
    if (initialized.current || !display) return;

    initialized.current = true;
    loadTickets({ withLoad: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [display]);

  return (
    <TicketsContext.Provider
      value={{
        tickets: brokenTickets,
        loading,
        currentPage,
        pagesCount,
        isAndroidDevice: true,
        resetDataToInitialValue,
        changeTickets: setTickets,
        updateTicket,
        receiveTicketFromSocket,
        receiveTicketStatusChangeFromSocketFn,
        receiveTicketItemStatusChangeFromSocketFn,
        receiveTicketSubItemStatusChangeFromSocketFn,
        receiveTicketItemReplaceFromSocketFn,
        receiveTicketItemExtractFromSocketFn,
        receiveTicketSubItemExtractFromSocketFn,
        loadTickets,
      }}
    >
      {children}
    </TicketsContext.Provider>
  );
};

const useTickets = (): ITicketsContextData => {
  return useContext(TicketsContext);
};

export { TicketsProvider, useTickets };
